/** Live messages share Activity; no second conversation or tool-output view. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { MessageComposer, MessageSendButton } from '@/components/MessageComposer'
import type { TraceEntry } from './types'

type Messages = { entries: TraceEntry[]; can_send: boolean }
type PendingMessage = { id: string; message: string }

function savedRequest(key: string): PendingMessage | undefined {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || 'null')
    if (typeof value?.id === 'string' && typeof value?.message === 'string') return value
  } catch { /* Ignore invalid saved state. */ }
}

export function useBuilderMessages(taskId: string, jobId: string, live: boolean) {
  const path = `/eng/tasks/${taskId}/authoring/${jobId}/messages`
  const draftKey = `builder-message:${taskId}/${jobId}`
  const requestKey = `${draftKey}:pending`
  const [view, setView] = useState<Messages>({ entries: [], can_send: false })
  const [text, setText] = useState(() => sessionStorage.getItem(draftKey) || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const request = useRef<PendingMessage | undefined>(savedRequest(requestKey))
  const sending = useRef(false)
  const load = useCallback(async () => {
    const value = await api.get<Messages>(path)
    setView(value)
  }, [path])

  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    async function poll() {
      try {
        const value = await api.get<Messages>(path)
        if (!stopped) setView(value)
      } catch { /* Keep saved messages visible during a temporary connection failure. */ }
      if (!stopped && live) timer = setTimeout(poll, 3000)
    }
    void poll()
    return () => { stopped = true; clearTimeout(timer) }
  }, [path, live])

  async function send() {
    const message = text.trim()
    if (!message || sending.current || !view.can_send) return
    sending.current = true; setBusy(true); setError('')
    if (request.current?.message !== message) request.current = { id: crypto.randomUUID(), message }
    try {
      // Save before sending: a lost response must reuse the ID even after reload.
      sessionStorage.setItem(requestKey, JSON.stringify(request.current))
      await api.post(path, request.current)
      setText(''); sessionStorage.removeItem(draftKey); sessionStorage.removeItem(requestKey); request.current = undefined
      await load().catch(() => {})
    } catch (e) { setError(String(e)) }
    finally { sending.current = false; setBusy(false) }
  }

  return { entries: view.entries, composer: live && view.can_send ? <>
    <form className="builder-message" onSubmit={event => { event.preventDefault(); void send() }}>
      <MessageComposer compact actions={<MessageSendButton busy={busy} disabled={busy || !text.trim()} />}>
      <textarea aria-label="Message the agent" placeholder="Message the agent…" rows={2} maxLength={4000}
        value={text} disabled={busy} onChange={event => {
          setText(event.target.value); sessionStorage.setItem(draftKey, event.target.value)
        }} onKeyDown={event => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void send() }
        }} />
      </MessageComposer>
    </form>
    {error && <p className="muted small" role="alert">{error}</p>}
  </> : null }
}
